import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { useProjectByShortId } from '@/features/team/hooks'
import { MarkdownPreview, Logo } from '@/shared/components'

/**
 * Scoping Preview Page - Full-page printable view
 * Displays all specs content in a clean, print-optimized layout
 * Accessible via /scoping/:shortId/preview
 */
export function ScopingPreviewPage() {
  const { t } = useTranslation()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: project, isLoading, error } = useProjectByShortId(shortId)

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    )
  }

  // Error / Not found
  if (error || !project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white">
        <p className="text-gray-500">{t('scopingV2.error.notFound')}</p>
        <a href="/dashboard" className="text-accent hover:underline">
          {t('scopingV2.error.backToDashboard')}
        </a>
      </div>
    )
  }

  const currentDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="min-h-screen bg-white">
      {/* Screen header - hidden on print */}
      <header className="border-b border-gray-200 px-8 py-6 print:hidden">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Logo className="h-5 w-5 text-white" />
            </div>
            <span className="text-sm font-medium text-foreground">heynicolai.com</span>
          </div>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">
            {project.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('scopingPreview.subtitle', {
              version: project.current_version || 1,
            })}
          </p>
        </div>
      </header>

      {/* Print header - only visible on print */}
      <div className="hidden border-b border-gray-200 px-8 py-6 print:block">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-primary print:bg-[#1E293B]">
            <Logo className="h-4 w-4 text-white" />
          </div>
          <span className="text-sm text-gray-500">
            heynicolai.com — {t('scopingPreview.specsDocument')}
          </span>
        </div>
        <h1 className="mt-2 text-2xl font-bold text-gray-900">{project.name}</h1>
        <p className="mt-1 text-sm text-gray-500">
          Version {project.current_version || 1} — {currentDate}
        </p>
      </div>

      {/* Content */}
      <main className="mx-auto max-w-4xl px-8 py-8">
        {project.specs_content ? (
          <div className="prose-preview">
            <MarkdownPreview content={project.specs_content} />
          </div>
        ) : (
          <p className="py-12 text-center italic text-gray-500">
            {t('scopingPreview.noContent')}
          </p>
        )}
      </main>

      {/* Screen footer - hidden on print */}
      <footer className="border-t border-gray-200 px-8 py-4 text-center text-sm text-gray-400 print:hidden">
        {t('scopingPreview.footer')}
      </footer>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            margin: 2cm;
          }

          body {
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print\\:hidden {
            display: none !important;
          }

          .print\\:block {
            display: block !important;
          }

          /* Typography improvements for print */
          .prose-preview h1,
          .prose-preview h2,
          .prose-preview h3 {
            page-break-after: avoid;
          }

          .prose-preview table,
          .prose-preview figure,
          .prose-preview pre {
            page-break-inside: avoid;
          }

          .prose-preview p {
            orphans: 3;
            widows: 3;
          }
        }
      `}</style>
    </div>
  )
}
